/**
 * Admin Panel - Dashboard Viewer
 *
 * Platform owner-only page for viewing any organization's dashboard.
 * Features:
 * - Organization selector dropdown — an EXPLICIT choice on this page (the
 *   selector or `?orgId=`), never a default: nothing is shown until one is
 *   made (#1341). When the page's choice CHANGES it is made the app's one
 *   organization selection (`selectOrganization`) — the dashboards below fetch
 *   with its `X-Selected-Org-ID`, the switch clears the previous org's cache —
 *   and the dashboard renders only once the two agree, so the «Просмотр: X»
 *   line and the data are always the same organization. The other direction is
 *   not fought: when the app's selection changes from elsewhere (the sidebar
 *   switcher) the page FOLLOWS it — its choice becomes that organization and
 *   `?orgId=` is rewritten (replace), so a reload keeps it. While a choice
 *   exists and the organization list is being reloaded after a switch, the
 *   dashboard skeleton is shown, never «select an organization first». Any
 *   organization: the owner need not be a member (the server admits the
 *   platform owner to every org).
 * - Dashboard type selector (Owner, Client, Admin, etc.)
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LayoutDashboard, Building2, Users, FileText, DollarSign } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { ListSkeleton, DashboardSkeleton } from '@/components/ui/skeletons';
import { useToast } from '@/contexts/ToastContext';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { formatCents } from '@/lib/currency';
import { useOrganization } from '@/contexts/OrganizationContext';
import { logError } from '@/lib/logger';
import type { AdminOrganization } from '@/lib/api';
import { AdminLayout } from './components/AdminLayout';
import { useExplicitAdminOrg } from './hooks/useExplicitAdminOrg';
import { useAdminOrganizations } from './hooks/useAdminOrganizations';
import { fetchOrgFinancialReport } from '@/lib/api/admin-financial-report';

// Lazy load dashboard components (same as main dashboard)
const OwnerDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.OwnerDashboard }))
);
const ClientDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.ClientDashboard }))
);
const AdminDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.AdminDashboard }))
);
const AgentDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.AgentDashboard }))
);
const SupervisorDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.SupervisorDashboard }))
);
const OrganizationDashboard = lazy(() =>
  import('@/components/dashboards').then((m) => ({ default: m.OrganizationDashboard }))
);

export const Route = createFileRoute('/admin/dashboard')({
  component: AdminDashboardPage,
  // `?orgId=` — an explicit choice (the org-health matrix links here).
  validateSearch: (search: Record<string, unknown>) => ({
    orgId: typeof search.orgId === 'string' ? search.orgId : undefined,
  }),
});

type DashboardType =
  'owner' | 'client' | 'admin' | 'agent' | 'supervisor' | 'organization';

const getDashboardOptions = (
  t: (key: string, options?: Record<string, unknown>) => string
): { value: DashboardType; label: string }[] => [
  { value: 'client', label: t('dashboard:widgets.clientDashboard') },
  { value: 'organization', label: t('dashboard:organizationView') },
  { value: 'admin', label: t('dashboard:widgets.adminDashboard') },
  { value: 'owner', label: t('dashboard:ownerDashboard') },
  { value: 'supervisor', label: t('dashboard:widgets.supervisorDashboard') },
  { value: 'agent', label: t('dashboard:widgets.agentDashboard') },
];

function AdminDashboardPage() {
  return (
    <AdminLayout hideSectionTitle>
      <AdminDashboardContent />
    </AdminLayout>
  );
}

interface AdminSelectedDashboardProps {
  selectedOrgId: string;
  dashboardType: DashboardType;
  t: (key: string) => string;
}

function AdminSelectedDashboard({
  selectedOrgId,
  dashboardType,
  t,
}: AdminSelectedDashboardProps) {
  if (!selectedOrgId) {
    return (
      <Card className="p-8 text-center">
        <LayoutDashboard className="h-12 w-12 mx-auto text-fg-muted mb-4" />
        <p className="text-fg-muted">{t('selectOrgFirst')}</p>
      </Card>
    );
  }

  switch (dashboardType) {
    case 'owner':
      return <OwnerDashboard />;
    case 'client':
      return <ClientDashboard />;
    case 'admin':
      return <AdminDashboard />;
    case 'agent':
      return <AgentDashboard />;
    case 'supervisor':
      return <SupervisorDashboard />;
    case 'organization':
      return <OrganizationDashboard />;
    default:
      return <ClientDashboard />;
  }
}

function AdminDashboardContent() {
  const { t } = useTranslation(['admin', 'dashboard', 'common']);
  const { toast } = useToast();
  const {
    selectedOrgId: globalOrgId,
    selectOrganization,
    isOrgSwitching,
  } = useOrganization();
  const { authenticatedFetch } = useAuthFetch();
  const navigate = useNavigate();
  const {
    organizations,
    isLoading: loading,
    isFetching: fetchingOrgs,
    error,
  } = useAdminOrganizations();
  const {
    selectedOrgId: chosenOrgId,
    candidateOrgId,
    selectOrg,
  } = useExplicitAdminOrg(organizations);

  // The last choice this page pushed into (or found already in) the app's
  // selection, and the app's selection as of the previous run.
  const appliedChoiceRef = useRef('');
  const previousGlobalRef = useRef(globalOrgId);

  useEffect(() => {
    const previousGlobal = previousGlobalRef.current;
    previousGlobalRef.current = globalOrgId;

    // The page's choice CHANGED: it becomes the app's selection (what the
    // dashboards' requests carry). Until the debounced switch lands the global
    // value is still the old one — that is not a change from elsewhere.
    if (chosenOrgId && chosenOrgId !== appliedChoiceRef.current) {
      appliedChoiceRef.current = chosenOrgId;
      if (chosenOrgId !== globalOrgId) selectOrganization(chosenOrgId);
      return;
    }

    // The app's selection changed from elsewhere (the sidebar switcher; a
    // null → org change is the roster restore, not a switch): follow it, in
    // the address too, so a reload keeps it.
    if (
      candidateOrgId &&
      previousGlobal &&
      globalOrgId &&
      globalOrgId !== previousGlobal &&
      globalOrgId !== candidateOrgId
    ) {
      selectOrg(globalOrgId);
      void navigate({
        to: '/admin/dashboard',
        search: { orgId: globalOrgId },
        replace: true,
      });
    }
  }, [chosenOrgId, candidateOrgId, globalOrgId, selectOrganization, selectOrg, navigate]);

  // Shown only once the request header is the chosen organization.
  const selectedOrgId =
    chosenOrgId && chosenOrgId === globalOrgId && !isOrgSwitching ? chosenOrgId : '';
  // A choice exists but its dashboard cannot be shown yet: the switch is in
  // flight, or the switch wiped the organization list and it is being
  // refetched — the choice cannot be told from «none» until it is back.
  const switching =
    !!candidateOrgId &&
    (chosenOrgId ? !selectedOrgId : loading || fetchingOrgs || isOrgSwitching);

  // Current calendar month (month-to-date) for the platform KPI aggregates.
  // Computed once per mount so the query key stays stable across re-renders.
  const monthRange = useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { periodStart: start.toISOString(), periodEnd: now.toISOString() };
  }, []);

  // Platform-wide records & billed revenue this month. Reuses the same
  // aggregate the Reports tab uses (/admin/reports/financial/orgs totals).
  const platformTotalsQuery = useQuery({
    queryKey: [
      'admin',
      'dashboard',
      'platform-totals',
      monthRange.periodStart,
      monthRange.periodEnd,
    ],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<{ records: number; revenueCents: number }> =>
      (await fetchOrgFinancialReport(authenticatedFetch, monthRange)).totals,
  });

  // Total users across every organization (sum of per-org user counts already
  // present in the organizations list — no extra request).
  const totalUsers = useMemo(
    () => organizations.reduce((sum, o) => sum + (o.userCount ?? 0), 0),
    [organizations]
  );

  const [dashboardType, setDashboardType] = useState<DashboardType>('client');

  useEffect(() => {
    if (!error) return;
    logError('admin.dashboard.organizations_fetch_failed', {
      component: 'AdminDashboard',
      error: error instanceof Error ? error.message : 'unknown_error',
    });
    toast.error(
      t('failedToLoadOrganizations'),
      error instanceof Error ? error.message : t('common:errors.unknown')
    );
  }, [error, toast, t]);

  // Platform KPI aggregates are secondary — log a failure (cards degrade to
  // "—") but don't raise a toast, to avoid noise on the overview screen.
  useEffect(() => {
    const totalsError = platformTotalsQuery.error;
    if (!totalsError) return;
    logError('admin.dashboard.platform_totals_fetch_failed', {
      component: 'AdminDashboard',
      error: totalsError instanceof Error ? totalsError.message : 'unknown_error',
    });
  }, [platformTotalsQuery.error]);

  // Get selected organization
  const selectedOrg = organizations.find(
    (o: AdminOrganization) => o.id === selectedOrgId
  );
  const dashboardOptions = getDashboardOptions(t);

  // KPI display values — null renders an em-dash + "no data" aria-label.
  const noDataLabel = t('admin:dashboard.noData', 'No data available');
  const usersDisplay = loading ? null : totalUsers;
  const recordsDisplay = platformTotalsQuery.data?.records ?? null;
  const revenueDisplay = platformTotalsQuery.data
    ? formatCents(platformTotalsQuery.data.revenueCents)
    : null;

  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      {/* KPI Overview Cards */}
      <div
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="admin-dashboard-kpi"
      >
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary-dim">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-fg-muted">
                {t('admin:dashboard.organizations', 'Organizations')}
              </p>
              <p className="text-xl font-semibold text-fg tabular-nums">
                {organizations.length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-info-dim">
              <Users className="h-5 w-5 text-info" />
            </div>
            <div>
              <p className="text-xs text-fg-muted">
                {t('admin:dashboard.users', 'Users')}
              </p>
              <p
                className="text-xl font-semibold text-fg tabular-nums"
                aria-label={
                  usersDisplay === null
                    ? t('admin:dashboard.users', 'Users') + ': ' + noDataLabel
                    : undefined
                }
              >
                {usersDisplay ?? '—'}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success-dim">
              <FileText className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-xs text-fg-muted">
                {t('admin:dashboard.recordsMonth', 'Records / Month')}
              </p>
              <p
                className="text-xl font-semibold text-fg tabular-nums"
                aria-label={
                  recordsDisplay === null
                    ? t('admin:dashboard.recordsMonth', 'Records / Month') +
                      ': ' +
                      noDataLabel
                    : undefined
                }
              >
                {recordsDisplay ?? '—'}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-warning-dim">
              <DollarSign className="h-5 w-5 text-warning" />
            </div>
            <div>
              <p className="text-xs text-fg-muted">
                {t('admin:dashboard.revenueMonth', 'Revenue / Month')}
              </p>
              <p
                className="text-xl font-semibold text-fg tabular-nums"
                aria-label={
                  revenueDisplay === null
                    ? t('admin:dashboard.revenueMonth', 'Revenue / Month') +
                      ': ' +
                      noDataLabel
                    : undefined
                }
              >
                {revenueDisplay ?? '—'}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Controls */}
      <Card className="p-4" data-testid="admin-dashboard-controls">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Organization Selector */}
          <div className="flex-1 min-w-[200px] space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-fg">
              <Building2 className="h-4 w-4 text-fg-muted" />
              {t('organization')}
            </label>
            {loading ? (
              <div className="py-2">
                <ListSkeleton items={1} />
              </div>
            ) : (
              <Select
                value={chosenOrgId}
                onValueChange={(val) => {
                  const orgId = val as string;
                  selectOrg(orgId);
                  // The address carries the choice, so a reload keeps it
                  // instead of reopening an older `?orgId=`.
                  void navigate({
                    to: '/admin/dashboard',
                    search: { orgId },
                    replace: true,
                  });
                }}
                placeholder={t('selectOrgPlaceholder')}
                options={organizations.map((org) => ({
                  value: org.id,
                  label: `${org.name}${org.bitrix24Domain ? ` (${org.bitrix24Domain})` : ''}`,
                }))}
                size="sm"
                data-testid="admin-dashboard-org-select"
              />
            )}
          </div>

          {/* Dashboard Type Selector */}
          <div className="flex-1 min-w-[200px] space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-fg">
              <LayoutDashboard className="h-4 w-4 text-fg-muted" />
              {t('dashboardType')}
            </label>
            <Select
              value={dashboardType}
              onValueChange={(val) => setDashboardType(val as DashboardType)}
              options={dashboardOptions.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              size="sm"
              data-testid="admin-dashboard-type-select"
            />
          </div>
        </div>

        {/* Selected org info */}
        {selectedOrg && (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-sm text-fg-muted">
              {t('viewing')}:{' '}
              <span className="font-medium text-fg">{selectedOrg.name}</span>
              {selectedOrg.bitrix24Domain && (
                <span className="text-fg-subtle"> ({selectedOrg.bitrix24Domain})</span>
              )}
            </p>
          </div>
        )}
      </Card>

      {/* Dashboard Content */}
      {switching ? (
        <DashboardSkeleton />
      ) : (
        <Suspense fallback={<DashboardSkeleton />}>
          {/* Keyed by the org: a switch unmounts the previous org's open
              details (drawers, popovers) along with its data. */}
          <AdminSelectedDashboard
            key={selectedOrgId || 'none'}
            selectedOrgId={selectedOrgId}
            dashboardType={dashboardType}
            t={t}
          />
        </Suspense>
      )}
    </div>
  );
}
